import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { ConfirmDialog, EmptyState, IconButton, Link, MenuButton, Spinner, navigate, useContextMenu, useLocation, useToast, type MenuEntry } from '@dugzi/ui';
import { Calendar, Check, ChevronDown, ChevronRight, Copy, Download, ExternalLink, Eye, File as FileGeneric, FolderInput, FolderOpen, History, Info, LayoutGrid, Link2, List, MoreVertical, Pencil, RotateCcw, Share2, SortAsc, Star, StarOff, Trash2, Upload, User, X } from 'lucide-react';
import { api, withAccount, writesSettled } from '../lib/api';
import { sortNodes, when, type SortKey } from '../lib/format';
import type { Crumb, DriveNode, Me, Role } from '../lib/types';
import * as uploads from '../lib/uploads';
import { loadFolder } from '../lib/folder';
import { NeedAccess } from './NeedAccess';
import { FileView, type FileViewHandle } from './FileView';
import { DRAG_TYPE, dropMove, startDrag } from './dnd';
import { gPending } from '../lib/keys';
import { RenameDialog } from './RenameDialog';
import { FileIcon } from './FileIcon';
import type { FolderCtx } from './Shell';

// The viewer and pdf.js aren't part of the first visit (D-102); they're prefetched when the page is idle,
// so the first file someone opens doesn't wait for them.
const loadPreview = () => import('./Preview');
const Preview = lazy(loadPreview);
function prefetchViewer() {
  const go = () => { void loadPreview().catch(() => {}); void import('./PdfView').catch(() => {}); };
  const w = window as Window & { requestIdleCallback?(cb: () => void, o?: { timeout: number }): number };
  if (w.requestIdleCallback) w.requestIdleCallback(go, { timeout: 2000 }); else setTimeout(go, 300);
}
const ShareDialog = lazy(() => import('./ShareDialog'));
const MoveDialog = lazy(() => import('./MoveDialog'));
const Details = lazy(() => import('./Details'));

export type View =
  | { kind: 'folder'; id: string }
  | { kind: 'recent' | 'starred' | 'shared' | 'trash' }
  | { kind: 'search'; q: string; type: string; modified: string };

const TITLES = { recent: 'Recent', starred: 'Starred', shared: 'Shared with me', trash: 'Trash', search: 'Search' } as const;

// Filter chips above every list, as in Google Drive (CEO, 4 Oct 2026: "better use of space").
const TYPE_OPTS: [string, string][] = [['any', 'Any type'], ['folder', 'Folders'], ['document', 'Documents'], ['spreadsheet', 'Spreadsheets'], ['presentation', 'Presentations'], ['pdf', 'PDFs'], ['image', 'Pictures'], ['video', 'Videos'], ['audio', 'Audio'], ['archive', 'Archives']];
const PEOPLE_OPTS: [string, string][] = [['any', 'Anyone'], ['me', 'Owned by me'], ['others', 'Not owned by me']];
const MODIFIED_OPTS: [string, string][] = [['any', 'Any time'], ['today', 'Today'], ['week', 'Last 7 days'], ['month', 'Last 30 days'], ['year', 'This year']];
type Filters = { type: string; people: string; modified: string };
const NO_FILTERS: Filters = { type: 'any', people: 'any', modified: 'any' };

function modifiedSince(v: string): number {
  const now = new Date();
  if (v === 'today') return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (v === 'week') return now.getTime() - 7 * 86400e3;
  if (v === 'month') return now.getTime() - 30 * 86400e3;
  if (v === 'year') return new Date(now.getFullYear(), 0, 1).getTime();
  return 0;
}

/** One filter chip: its name, or the value picked with a cross to clear it. */
function Chip({ label, icon, opts, value, onChange, testId }: { label: string; icon: React.ReactNode; opts: [string, string][]; value: string; onChange(v: string): void; testId: string }) {
  const on = value !== 'any';
  const picked = opts.find(([v]) => v === value)?.[1] ?? label;
  return (
    <span className={`chip${on ? ' is-on' : ''}`} data-testid={testId} data-value={value}>
      <MenuButton items={opts.map(([v, l]) => ({ label: l, checked: v === value, onSelect: () => onChange(v), testId: `${testId}-${v}` }))} label={on ? `${label}: ${picked}` : label} menuLabel={label} className="chip__btn" testId={`${testId}-btn`}>
        {on ? <Check aria-hidden size={16} /> : icon}<span>{on ? picked : label}</span>{!on && <ChevronDown aria-hidden size={16} />}
      </MenuButton>
      {on && <button type="button" className="chip__clear" aria-label={`Clear ${label.toLowerCase()} filter`} onClick={() => onChange('any')} data-testid={`${testId}-clear`}><X aria-hidden size={14} /></button>}
    </span>
  );
}

function load<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v ? (JSON.parse(v) as T) : fallback; } catch { return fallback; }
}
function save(key: string, v: unknown) { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* fine */ } }

interface Props {
  me: Me;
  view: View;
  refresh: number;
  onChanged(): void;
  onFolder(f: FolderCtx | null): void;
  onUpload(): void;
  onNewFolder(): void;
}

export function Browser({ me, view, refresh, onChanged, onFolder, onUpload, onNewFolder }: Props) {
  const toast = useToast();
  const { search } = useLocation();
  const [nodes, setNodes] = useState<DriveNode[] | null>(null);
  const [path, setPath] = useState<Crumb[]>([]);
  const [role, setRole] = useState<Role>('owner');
  const [error, setError] = useState<string | null>(null);
  // A folder this account can't open (or that's gone: the API doesn't say which) shows You need access (DZ-10803).
  const [noAccess, setNoAccess] = useState(false);
  const [mode, setMode] = useState<'list' | 'grid'>(() => load('dz.drive.mode', 'list'));
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>(() => load('dz.drive.sort', { key: 'name', desc: false }));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [active, setActive] = useState(-1);
  const anchor = useRef(-1);
  // A finger or a pen opens on a tap and selects by a long press (the context menu's, which selects the row first), as
  // Google Drive and OneDrive do on phones and tablets; a mouse selects on a click and opens on a double-click. The last
  // pointer down decides, read on every press: a laptop with a touch screen gets both (DZ-10796).
  const touch = useRef(false);
  useEffect(() => {
    const on = (e: PointerEvent) => { touch.current = e.pointerType !== 'mouse'; };
    window.addEventListener('pointerdown', on, true);
    return () => window.removeEventListener('pointerdown', on, true);
  }, []);
  const [preview, setPreview] = useState<string | null>(null);
  const [rename, setRename] = useState<DriveNode | null>(null);
  const [share, setShare] = useState<DriveNode | null>(null);
  const [move, setMove] = useState<DriveNode[] | null>(null);
  const [details, setDetails] = useState<DriveNode | null>(null);
  const [forever, setForever] = useState<DriveNode[] | null>(null);
  const [emptyTrash, setEmptyTrash] = useState(false);
  const [dropOver, setDropOver] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [quick, setQuick] = useState<DriveNode[]>([]);
  const [quickOpen, setQuickOpen] = useState<boolean>(() => load('dz.drive.quick', true));
  const list = useRef<FileViewHandle>(null);
  const ctx = useContextMenu();
  const viewKey = JSON.stringify(view);
  const trash = view.kind === 'trash';
  const canEdit = view.kind === 'folder' ? role === 'owner' || role === 'editor' : !trash;

  // The place on screen now: a load for a place the person has already left is dropped.
  const shownView = useRef(viewKey);
  shownView.current = viewKey;
  const fetchData = useCallback(async (quiet = false) => {
    if (!quiet) { setError(null); setNoAccess(false); }
    const key = viewKey;
    const current = () => shownView.current === key;
    try {
      await writesSettled();
      if (!current()) return;
      if (view.kind === 'folder') {
        let shown = false;
        const r = await loadFolder(view.id, (page) => {
          if (!current()) return;
          setNodes(page.children);
          if (!shown) { shown = true; setPath(page.path); setRole(page.role); }
        }, () => !current());
        if (!current()) return;
        const name = r.path[r.path.length - 1]?.name ?? 'My Drive';
        onFolder({ id: view.id === 'root' ? me.rootId : view.id, canEdit: r.role === 'owner' || r.role === 'editor', name });
        document.title = `${name} · Dugzi Drive`;
      } else if (view.kind === 'search') {
        const p = new URLSearchParams();
        if (view.q) p.set('q', view.q);
        if (view.type !== 'any') p.set('type', view.type);
        if (view.modified !== 'any') p.set('modified', view.modified);
        const r = await api<{ data: DriveNode[] }>(`/api/search?${p}`);
        if (!current()) return;
        setNodes(r.data); setPath([]); setRole('viewer'); onFolder(null);
        document.title = `Search · Dugzi Drive`;
      } else {
        const r = await api<{ data: DriveNode[] }>(`/api/views/${view.kind}`);
        if (!current()) return;
        setNodes(r.data); setPath([]); setRole('viewer'); onFolder(null);
        document.title = `${TITLES[view.kind]} · Dugzi Drive`;
      }
    } catch (e) {
      if (!current()) return;
      if (view.kind === 'folder' && (e as { status?: number }).status === 404) { setNoAccess(true); return; }
      setError((e as Error).message); setNodes((n) => n ?? []);
    }
  }, [viewKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(prefetchViewer, []);
  // A new place: clear, then load (search waits for typing to pause).
  useEffect(() => {
    setSelected(new Set()); setActive(-1); anchor.current = -1;
    if (view.kind !== 'search') { setNodes(null); setFilters(NO_FILTERS); }
    const t = setTimeout(() => fetchData(), view.kind === 'search' ? 180 : 0);
    return () => clearTimeout(t);
  }, [viewKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (refresh) fetchData(true); }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live updates from uploads into this folder.
  useEffect(() => uploads.onUploaded(() => fetchData(true)), [fetchData]);

  // The search view filters by type and date on the server (from the address); every other list here.
  const shown = useMemo(() => {
    if (!nodes) return [];
    const since = view.kind === 'search' ? 0 : modifiedSince(filters.modified);
    const list = nodes.filter((n) => (view.kind === 'search' || filters.type === 'any' || n.type === filters.type)
      && (filters.people === 'any' || (filters.people === 'me') === n.owner.you)
      && (!since || new Date(n.modifiedAt).getTime() >= since));
    if (view.kind === 'recent' || view.kind === 'search') return list;
    return sortNodes(list, sort.key, sort.desc);
  }, [nodes, sort, view.kind, filters]);
  const filtered = filters.type !== 'any' || filters.people !== 'any' || filters.modified !== 'any';

  // Quick access on My Drive: the files touched last, as chips above the list (Google's "Suggested").
  const atRoot = view.kind === 'folder' && view.id === 'root';
  useEffect(() => {
    if (!atRoot) return;
    let live = true;
    api<{ data: DriveNode[] }>('/api/views/recent').then((r) => { if (live) setQuick(r.data.filter((n) => n.kind === 'file').slice(0, 6)); }).catch(() => {});
    return () => { live = false; };
  }, [atRoot, refresh]);

  // ?preview=fil_… (a file link): open it once the list is here.
  useEffect(() => {
    const id = new URLSearchParams(search).get('preview');
    if (id && nodes) { setPreview(id); const u = new URL(location.href); u.searchParams.delete('preview'); history.replaceState(history.state, '', u.pathname + u.search); }
  }, [nodes, search]);

  const sel = useMemo(() => shown.filter((n) => selected.has(n.id)), [shown, selected]);
  const files = useMemo(() => shown.filter((n) => n.kind === 'file'), [shown]);

  const setSort2 = (k: SortKey) => setSort((s) => { const v = { key: k, desc: s.key === k ? !s.desc : k === 'modified' || k === 'size' }; save('dz.drive.sort', v); return v; });
  const setMode2 = (m: 'list' | 'grid') => { setMode(m); save('dz.drive.mode', m); };

  // ---------- Actions ----------
  const open = useCallback((n: DriveNode) => {
    if (trash) { toast.show('Restore it from the trash to open it.'); return; }
    if (n.kind === 'folder') navigate(`/folders/${n.id}`);
    else setPreview(n.id);
  }, [trash, toast]);

  const download = (ns: DriveNode[]) => {
    const fs = ns.filter((n) => n.kind === 'file');
    if (!fs.length) { toast.show('Folders can’t be downloaded yet. Open the folder and choose its files.'); return; }
    fs.forEach((n, i) => setTimeout(() => {
      const a = document.createElement('a');
      a.href = withAccount(`/api/nodes/${n.id}/download`);
      a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
    }, i * 400));
  };

  const copyLink = async (n: DriveNode) => {
    try {
      const l = await api<{ url: string }>(`/api/nodes/${n.id}/links`, { body: {} });
      await navigator.clipboard?.writeText(l.url).catch(() => {});
      toast.show('Link copied. Anyone with the link can view.', { action: { label: 'Settings', onClick: () => setShare(n) } });
      fetchData(true);
    } catch (e) { toast.show((e as Error).message); }
  };

  const star = async (ns: DriveNode[], on: boolean) => {
    try {
      await Promise.all(ns.map((n) => api(`/api/nodes/${n.id}`, { method: 'PATCH', body: { starred: on } })));
      setNodes((cur) => cur?.map((x) => (ns.some((n) => n.id === x.id) ? { ...x, starred: on } : x)).filter((x) => view.kind !== 'starred' || x.starred) ?? cur);
    } catch (e) { toast.show((e as Error).message); }
  };

  const toTrash = async (ns: DriveNode[]) => {
    if (!ns.length) return;
    const ids = ns.map((n) => n.id);
    try {
      await api('/api/nodes/trash', { body: { ids } });
      setSelected(new Set());
      toast.show(ns.length === 1 ? `“${ns[0].name}” moved to the trash.` : `${ns.length} items moved to the trash.`, {
        action: { label: 'Undo', onClick: () => { api('/api/nodes/restore', { body: { ids } }).then(() => { fetchData(true); onChanged(); }).catch((e) => toast.show(e.message)); } },
      });
      fetchData(true); onChanged();
    } catch (e) { toast.show((e as Error).message); }
  };

  const restore = async (ns: DriveNode[]) => {
    try { await api('/api/nodes/restore', { body: { ids: ns.map((n) => n.id) } }); toast.show(ns.length === 1 ? 'Restored.' : `${ns.length} items restored.`); setSelected(new Set()); fetchData(true); onChanged(); } catch (e) { toast.show((e as Error).message); }
  };

  const deleteForever = async (ns: DriveNode[]) => {
    try { await api('/api/nodes/delete', { body: { ids: ns.map((n) => n.id) } }); toast.show('Deleting. Big folders can take a while.'); setSelected(new Set()); fetchData(true); onChanged(); } catch (e) { toast.show((e as Error).message); }
  };

  const makeCopy = async (n: DriveNode) => {
    try { const c = await api<DriveNode>(`/api/nodes/${n.id}/copy`, { body: {} }); toast.show(`Made “${c.name}”.`); fetchData(true); onChanged(); } catch (e) { toast.show((e as Error).message); }
  };

  const editorFor = (n: DriveNode) => {
    const app = n.type === 'document' ? 'docs' : n.type === 'spreadsheet' ? 'sheets' : n.type === 'presentation' ? 'slides' : null;
    return app && me.openWith.includes(app) ? app : null;
  };
  const openIn = (n: DriveNode, app: string) => {
    const host = new URL(me.accountsUrl);
    host.hostname = host.hostname.replace(/^accounts\./, `${app}.`);
    window.open(`${host.origin}/open?drive=${n.id}&account=${encodeURIComponent(me.user.id)}`, '_blank', 'noopener');
  };

  const menuFor = (ns: DriveNode[]): MenuEntry[] => {
    if (!ns.length) return [];
    const one = ns.length === 1 ? ns[0] : null;
    if (trash && ns.some((n) => n.deletion)) return [];
    if (trash) return [
      { label: 'Restore', icon: <RotateCcw aria-hidden />, onSelect: () => restore(ns), testId: 'menu-restore' },
      { label: 'Delete for good', icon: <Trash2 aria-hidden />, danger: true, onSelect: () => setForever(ns), testId: 'menu-delete-forever' },
    ];
    const allStarred = ns.every((n) => n.starred);
    const editor = one && editorFor(one);
    return [
      one && { label: one.kind === 'folder' ? 'Open' : 'Preview', icon: one.kind === 'folder' ? <FolderOpen aria-hidden /> : <Eye aria-hidden />, shortcut: one.kind === 'folder' ? 'Enter' : 'Space', onSelect: () => open(one), testId: 'menu-open' },
      one && editor ? { label: `Open in ${editor[0].toUpperCase()}${editor.slice(1)}`, icon: <ExternalLink aria-hidden />, onSelect: () => openIn(one, editor) } : null,
      { separator: true },
      one && { label: 'Share', icon: <Share2 aria-hidden />, shortcut: '.', onSelect: () => setShare(one), testId: 'menu-share' },
      one && { label: 'Copy link', icon: <Link2 aria-hidden />, onSelect: () => copyLink(one), testId: 'menu-copy-link' },
      { separator: true },
      { label: 'Download', icon: <Download aria-hidden />, onSelect: () => download(ns), disabled: !ns.some((n) => n.kind === 'file'), testId: 'menu-download' },
      one && canEdit && { label: 'Rename', icon: <Pencil aria-hidden />, shortcut: 'F2', onSelect: () => setRename(one), testId: 'menu-rename' },
      canEdit && { label: 'Move to…', icon: <FolderInput aria-hidden />, shortcut: 'M', onSelect: () => setMove(ns), testId: 'menu-move' },
      one && one.kind === 'file' && { label: 'Make a copy', icon: <Copy aria-hidden />, onSelect: () => makeCopy(one), testId: 'menu-copy' },
      { label: allStarred ? 'Remove star' : 'Add star', icon: allStarred ? <StarOff aria-hidden /> : <Star aria-hidden />, shortcut: 'S', onSelect: () => star(ns, !allStarred), testId: 'menu-star' },
      one && { label: 'Details and access', icon: <Info aria-hidden />, shortcut: 'I', onSelect: () => setDetails(one), testId: 'menu-details' },
      one && one.kind === 'file' && { label: 'Versions', icon: <History aria-hidden />, onSelect: () => setDetails(one), testId: 'menu-versions' },
      { separator: true },
      canEdit && { label: 'Move to trash', icon: <Trash2 aria-hidden />, shortcut: 'Delete', danger: true, onSelect: () => toTrash(ns), testId: 'menu-trash' },
    ];
  };

  // ---------- Selection and keys ----------
  const selectOnly = (i: number) => { const n = shown[i]; if (!n) return; setSelected(new Set([n.id])); setActive(i); anchor.current = i; };
  const onPointerRow = (i: number, e: MouseEvent) => {
    if (e.button !== 0) { if (!selected.has(shown[i].id)) selectOnly(i); return; }
    // A touch's emulated mousedown: the tap's click decides (rowProps), so nothing is selected on the way to opening.
    if (touch.current) return;
    const n = shown[i];
    if (e.shiftKey && anchor.current >= 0) {
      const [a, b] = [Math.min(anchor.current, i), Math.max(anchor.current, i)];
      setSelected(new Set(shown.slice(a, b + 1).map((x) => x.id))); setActive(i);
    } else if (e.metaKey || e.ctrlKey) {
      setSelected((s) => { const x = new Set(s); if (x.has(n.id)) x.delete(n.id); else x.add(n.id); return x; });
      setActive(i); anchor.current = i;
    } else if (!selected.has(n.id) || selected.size > 1) {
      // Keep a multi-selection on press so it can be dragged; a click without dragging narrows it.
      if (!selected.has(n.id)) selectOnly(i); else setActive(i);
    }
    requestAnimationFrame(() => list.current?.focus());
  };

  const moveTo = (i: number, extend: boolean) => {
    const j = Math.max(0, Math.min(shown.length - 1, i));
    if (!shown[j]) return;
    if (extend && anchor.current >= 0) {
      const [a, b] = [Math.min(anchor.current, j), Math.max(anchor.current, j)];
      setSelected(new Set(shown.slice(a, b + 1).map((x) => x.id)));
      setActive(j);
    } else selectOnly(j);
    list.current?.scrollToIndex(j);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const cols = list.current?.columns() ?? 1;
    const cur = active < 0 ? -1 : active;
    const k = e.key;
    const mod = e.metaKey || e.ctrlKey;
    const step = ({ ArrowDown: mode === 'grid' ? cols : 1, ArrowUp: mode === 'grid' ? -cols : -1, ArrowRight: mode === 'grid' ? 1 : 0, ArrowLeft: mode === 'grid' ? -1 : 0, PageDown: 10 * cols, PageUp: -10 * cols } as Record<string, number>)[k];
    if (step !== undefined && step !== 0) { e.preventDefault(); moveTo(cur < 0 ? 0 : cur + step, e.shiftKey); return; }
    if (k === 'Home' || k === 'End') { e.preventDefault(); moveTo(k === 'Home' ? 0 : shown.length - 1, e.shiftKey); return; }
    if (mod && k.toLowerCase() === 'a') { e.preventDefault(); setSelected(new Set(shown.map((n) => n.id))); return; }
    if (k === 'Escape') { if (selected.size) { e.preventDefault(); setSelected(new Set()); } return; }
    if (k === 'ContextMenu' || (e.shiftKey && k === 'F10')) {
      e.preventDefault();
      const el = shown[cur] ? document.getElementById(`row-${shown[cur].id}`) : null;
      if (el) ctx.open(el, menuFor(sel.length ? sel : [shown[cur]]), 'File actions');
      return;
    }
    if (mod || e.altKey || k === 'g' || gPending()) return;
    const target = sel.length ? sel : shown[cur] ? [shown[cur]] : [];
    if (!target.length) { if (k === 'ArrowDown' || k === 'Enter') moveTo(0, false); return; }
    const one = target.length === 1 ? target[0] : null;
    if (k === 'Enter' && one) { e.preventDefault(); open(one); }
    else if (k === ' ' && one) { e.preventDefault(); if (one.kind === 'file' && !trash) setPreview(one.id); else open(one); }
    else if ((k === 'Delete' || k === 'Backspace') && canEdit) { e.preventDefault(); toTrash(target); }
    else if ((k === 'Delete' || k === 'Backspace') && trash) { e.preventDefault(); setForever(target); }
    else if (k === 'F2' && one && canEdit) { e.preventDefault(); setRename(one); }
    else if (k.toLowerCase() === 's' && !trash) { e.preventDefault(); star(target, !target.every((n) => n.starred)); }
    else if (k.toLowerCase() === 'm' && canEdit) { e.preventDefault(); setMove(target); }
    else if (k === '.' && one && !trash) { e.preventDefault(); setShare(one); }
    else if (k.toLowerCase() === 'i' && one) { e.preventDefault(); setDetails(one); }
    else if (k.toLowerCase() === 'v') { e.preventDefault(); setMode2(mode === 'list' ? 'grid' : 'list'); }
  };

  const rowProps = (i: number) => {
    const n = shown[i];
    const bound = ctx.bind(() => {
      if (!selected.has(n.id)) selectOnly(i);
      return menuFor(selected.has(n.id) ? sel : [n]);
    }, { label: 'File actions' }) as unknown as Record<string, unknown>;
    const folder = n.kind === 'folder';
    return {
      ...bound,
      onClick: (e: MouseEvent) => {
        if (e.shiftKey || e.metaKey || e.ctrlKey) return;
        if (touch.current) {
          // While something is selected (a long press started it), a tap adds or removes; in Trash a tap selects, so
          // Restore and Delete for good are at hand; otherwise a tap opens.
          if (selected.size || trash) {
            setSelected((s) => { const x = new Set(s); if (x.has(n.id)) x.delete(n.id); else x.add(n.id); return x; });
            setActive(i); anchor.current = i;
          } else open(n);
          return;
        }
        if (selected.size > 1 && selected.has(n.id)) selectOnly(i);
      },
      draggable: !trash && canEdit,
      onDragStart: (e: React.DragEvent) => {
        const ids = selected.has(n.id) ? sel.map((x) => x.id) : [n.id];
        if (!selected.has(n.id)) selectOnly(i);
        startDrag(e, ids, n.name);
      },
      ...(folder && !trash ? {
        'data-drop-folder': n.id,
        onDragOver: (e: React.DragEvent) => {
          const t = e.dataTransfer.types;
          if (t.includes(DRAG_TYPE) || t.includes('Files')) { e.preventDefault(); setDropOver(n.id); }
        },
        onDragLeave: () => setDropOver((d) => (d === n.id ? null : d)),
        onDrop: async (e: React.DragEvent) => {
          setDropOver(null);
          if (e.dataTransfer.types.includes(DRAG_TYPE)) {
            e.preventDefault();
            if (await dropMove(e.dataTransfer, n.id, toast)) { fetchData(true); onChanged(); }
          } else if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            const fs = await uploads.filesFromDrop(e.dataTransfer);
            if (fs.length) { uploads.enqueue(fs, n.id); toast.show(`Uploading to “${n.name}”.`); }
          }
        },
      } : {}),
    };
  };

  // ---------- Rendering ----------
  const title = view.kind === 'folder' ? null : TITLES[view.kind];
  const crumbs = view.kind === 'folder' && path.length > 0 ? (
    <nav aria-label="Folder path" className="crumbs">
      <ol>
        {path.map((c, i) => (
          <li key={c.id}>
            {i > 0 && <ChevronRight aria-hidden size={16} className="crumbs__sep" />}
            {i === path.length - 1
              ? <h1 className="crumbs__here" aria-current="page">{c.name}</h1>
              : <CrumbLink c={c} onMoved={() => { fetchData(true); onChanged(); }} />}
          </li>
        ))}
      </ol>
    </nav>
  ) : <h1 className="view__title">{title ?? 'My Drive'}</h1>;

  const one = sel.length === 1 ? sel[0] : null;
  const sortItems: MenuEntry[] = (['name', 'modified', 'size', 'type'] as SortKey[]).map((k) => ({
    label: { name: 'Name', modified: 'Last modified', size: 'Size', type: 'Type' }[k], checked: sort.key === k, onSelect: () => setSort2(k),
  }));

  const empty = error ? <p role="alert" className="dz-muted pad">{error}</p>
    : view.kind === 'folder' ? <EmptyState scene={path.length > 1 ? 'drive.folder.empty' : 'drive.first-files'} title={path.length > 1 ? 'This folder is empty' : 'Drop files here'} action={canEdit ? <button type="button" className="dz-btn dz-btn--primary" onClick={onUpload}><Upload aria-hidden size={18} />Upload files</button> : undefined}>
      {canEdit ? 'Drag files or folders here from your computer, or use New.' : 'Nothing has been added here yet.'}
    </EmptyState>
      : view.kind === 'trash' ? <EmptyState scene="drive.trash.empty" title="The trash is empty">Files you delete wait here for {me.limits.trashDays} days, then they’re deleted for good.</EmptyState>
        : view.kind === 'starred' ? <EmptyState scene="drive.starred.empty" title="Nothing starred yet">Star files and folders (press S) to find them here quickly.</EmptyState>
          : view.kind === 'shared' ? <EmptyState scene="drive.shared.empty" title="Nothing shared with you yet">When someone shares a file or folder with you, it shows up here.</EmptyState>
            : view.kind === 'recent' ? <EmptyState scene="drive.recent.empty" title="No recent files">Files you open, upload or change show up here.</EmptyState>
              : <p className="dz-muted pad">{view.kind === 'search' && view.q ? `Nothing matches “${view.q}”.` : 'Type to search your files.'}</p>;

  if (noAccess && view.kind === 'folder') return <NeedAccess me={me} kind="folder" id={view.id} inline />;
  return (
    <div className="browser">
      <div className="bar">
        <div className="bar__where">{crumbs}</div>
        <div className="bar__tools">
          {view.kind === 'folder' && canEdit && sel.length === 0 && (
            <button type="button" className="dz-btn dz-btn--sm hide-sm" onClick={onNewFolder} data-testid="new-folder">New folder</button>
          )}
          {trash && nodes && nodes.length > 0 && sel.length === 0 && (
            <button type="button" className="dz-btn dz-btn--sm" onClick={() => setEmptyTrash(true)} data-testid="empty-trash">Empty trash</button>
          )}
        </div>
      </div>
      <div className="chips" role="group" aria-label="Filters" data-testid="filter-chips">
        {view.kind === 'search' ? <>
          <Chip label="Type" icon={<FileGeneric aria-hidden size={16} />} opts={TYPE_OPTS} value={view.type} testId="filter-type"
            onChange={(v) => { const p = new URLSearchParams(search); p.set('type', v); navigate(`/search?${p}`, true); }} />
          <Chip label="People" icon={<User aria-hidden size={16} />} opts={PEOPLE_OPTS} value={filters.people} testId="filter-people" onChange={(v) => setFilters((f) => ({ ...f, people: v }))} />
          <Chip label="Modified" icon={<Calendar aria-hidden size={16} />} opts={MODIFIED_OPTS} value={view.modified} testId="filter-modified"
            onChange={(v) => { const p = new URLSearchParams(search); p.set('modified', v); navigate(`/search?${p}`, true); }} />
        </> : <>
          <Chip label="Type" icon={<FileGeneric aria-hidden size={16} />} opts={TYPE_OPTS} value={filters.type} testId="filter-type" onChange={(v) => setFilters((f) => ({ ...f, type: v }))} />
          <Chip label="People" icon={<User aria-hidden size={16} />} opts={PEOPLE_OPTS} value={filters.people} testId="filter-people" onChange={(v) => setFilters((f) => ({ ...f, people: v }))} />
          <Chip label="Modified" icon={<Calendar aria-hidden size={16} />} opts={MODIFIED_OPTS} value={filters.modified} testId="filter-modified" onChange={(v) => setFilters((f) => ({ ...f, modified: v }))} />
        </>}
        {filtered && view.kind !== 'search' && <button type="button" className="linkbtn chips__reset" onClick={() => setFilters(NO_FILTERS)} data-testid="filter-reset">Clear filters</button>}
        <span className="chips__end">
          <MenuButton items={sortItems} label="Sort" icon={<SortAsc aria-hidden size={18} />} testId="sort-menu" menuLabel="Sort by" />
          <span className="seg" role="group" aria-label="Layout">
            <button type="button" className={`seg__btn${mode === 'list' ? ' is-on' : ''}`} aria-pressed={mode === 'list'} aria-label="List view" title="List view (V)" onClick={() => setMode2('list')} data-testid={mode === 'list' ? 'view-list' : 'view-toggle'}>
              {mode === 'list' && <Check aria-hidden size={16} />}<List aria-hidden size={18} />
            </button>
            <button type="button" className={`seg__btn${mode === 'grid' ? ' is-on' : ''}`} aria-pressed={mode === 'grid'} aria-label="Grid view" title="Grid view (V)" onClick={() => setMode2('grid')} data-testid={mode === 'grid' ? 'view-grid' : 'view-toggle'}>
              {mode === 'grid' && <Check aria-hidden size={16} />}<LayoutGrid aria-hidden size={18} />
            </button>
          </span>
        </span>
      </div>
      {atRoot && quick.length > 0 && !filtered && (
        <section className="quick" aria-labelledby="quick-h" data-testid="quick-access">
          <h2 id="quick-h" className="quick__h">
            <button type="button" className="quick__toggle" aria-expanded={quickOpen} onClick={() => { setQuickOpen(!quickOpen); save('dz.drive.quick', !quickOpen); }} data-testid="quick-toggle">
              <ChevronDown aria-hidden size={18} className={quickOpen ? '' : 'is-closed'} />Suggested files
            </button>
          </h2>
          {quickOpen && (
            <ul className="quick__list">
              {quick.map((n) => (
                <li key={n.id}>
                  <button type="button" className="qchip" onClick={() => open(n)} onContextMenu={(e) => { e.preventDefault(); ctx.open({ x: e.clientX, y: e.clientY }, menuFor([n]), 'File actions'); }} data-testid="quick-file">
                    <FileIcon node={n} size={20} />
                    <span className="qchip__text"><span className="qchip__name">{n.name}</span><span className="qchip__why">{n.owner.you ? 'You changed' : 'Changed'} · {when(n.modifiedAt)}</span></span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {sel.length > 0 && (
        <div className="selbar" role="toolbar" aria-label={`${sel.length} selected`} data-testid="selection-bar">
          <IconButton label="Clear selection" icon={<X aria-hidden size={18} />} shortcut="Esc" onClick={() => setSelected(new Set())} />
          <span className="selbar__count" aria-live="polite">{sel.length} selected</span>
          {trash ? (sel.some((n) => n.deletion) ? <span className="selbar__count">Deleting</span> : <>
            <button type="button" className="dz-btn dz-btn--sm" onClick={() => restore(sel)} data-testid="restore"><RotateCcw aria-hidden size={16} />Restore</button>
            <button type="button" className="dz-btn dz-btn--sm dz-btn--ghost" onClick={() => setForever(sel)} data-testid="delete-forever">Delete for good</button>
          </>) : <>
            {one && <IconButton label="Share" shortcut="." icon={<Share2 aria-hidden size={18} />} onClick={() => setShare(one)} data-testid="sel-share" />}
            <IconButton label="Download" icon={<Download aria-hidden size={18} />} onClick={() => download(sel)} data-testid="sel-download" />
            {canEdit && <IconButton className="hide-sm" label="Move to…" shortcut="M" icon={<FolderInput aria-hidden size={18} />} onClick={() => setMove(sel)} data-testid="sel-move" />}
            {one && canEdit && <IconButton className="hide-sm" label="Rename" shortcut="F2" icon={<Pencil aria-hidden size={18} />} onClick={() => setRename(one)} data-testid="sel-rename" />}
            <IconButton className="hide-sm" label={sel.every((n) => n.starred) ? 'Remove star' : 'Add star'} shortcut="S" icon={<Star aria-hidden size={18} />} onClick={() => star(sel, !sel.every((n) => n.starred))} data-testid="sel-star" />
            {one && <IconButton className="hide-sm" label="Details and access" shortcut="I" icon={<Info aria-hidden size={18} />} onClick={() => setDetails(one)} data-testid="sel-details" />}
            {canEdit && <IconButton label="Move to trash" shortcut="Delete" icon={<Trash2 aria-hidden size={18} />} onClick={() => toTrash(sel)} data-testid="sel-trash" />}
            {/* On a phone the bar is one row: the rest of the actions are behind this (DZ-10797). */}
            <IconButton className="show-sm" label="More actions" icon={<MoreVertical aria-hidden size={18} />}
              onClick={(e: MouseEvent) => ctx.open(e.currentTarget as HTMLElement, menuFor(sel), 'File actions')} data-testid="sel-more" />
          </>}
        </div>
      )}
      {trash && <p className="dz-muted dz-small pad-x">Files in the trash are deleted for good after {me.limits.trashDays} days.</p>}
      {!nodes ? <div className="loading"><Spinner label="Loading your files" /></div> : (
        <FileView ref={list} nodes={shown} mode={mode} selected={selected} active={active}
          label={view.kind === 'folder' ? (path[path.length - 1]?.name ?? 'My Drive') : TITLES[view.kind]}
          sort={sort} onSort={setSort2} showOwner trash={trash}
          onPointerRow={onPointerRow} onOpen={(i) => open(shown[i])} onKeyDown={onKeyDown} rowProps={rowProps} empty={filtered && nodes.length ? <p className="dz-muted pad">Nothing here matches these filters. <button type="button" className="linkbtn" onClick={() => setFilters(NO_FILTERS)}>Clear filters</button></p> : empty} dropOver={dropOver}
          rowActions={(i) => {
            const n = shown[i];
            const stop = (f: () => void) => (e: MouseEvent) => { e.stopPropagation(); e.preventDefault(); f(); };
            return (
              // Pointer shortcuts: the keyboard has the same actions on the row (Shift+F10 or the Menu key
              // opens its menu; ., S, F2 and the rest), so these stay out of the tab order.
              <span className="rowacts__in" onMouseDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                {!trash && <span className="row__hover">
                  <IconButton label="Share" icon={<Share2 aria-hidden size={18} />} onClick={stop(() => setShare(n))} data-testid="row-share" tabIndex={-1} />
                  {n.kind === 'file' && <IconButton label="Download" icon={<Download aria-hidden size={18} />} onClick={stop(() => download([n]))} data-testid="row-download" tabIndex={-1} />}
                  {canEdit && <IconButton label="Rename" icon={<Pencil aria-hidden size={18} />} onClick={stop(() => setRename(n))} data-testid="row-rename" tabIndex={-1} />}
                  <IconButton label={n.starred ? 'Remove star' : 'Add star'} icon={n.starred ? <StarOff aria-hidden size={18} /> : <Star aria-hidden size={18} />} onClick={stop(() => star([n], !n.starred))} data-testid="row-star" tabIndex={-1} />
                </span>}
                <IconButton label="More actions" icon={<MoreVertical aria-hidden size={18} />} data-testid="row-more" tabIndex={-1}
                  onClick={(e: MouseEvent) => { e.stopPropagation(); if (!selected.has(n.id)) selectOnly(i); ctx.open(e.currentTarget as HTMLElement, menuFor(selected.has(n.id) ? sel : [n]), 'File actions'); }} />
              </span>
            );
          }} />
      )}
      {ctx.menu}
      <Suspense fallback={null}>
        {preview && <Preview me={me} files={files} id={preview} onClose={() => { setPreview(null); list.current?.focus(); }} onShare={(n) => setShare(n)} onOpenIn={openIn} editorFor={editorFor} />}
        {share && <ShareDialog me={me} node={share} onClose={() => { setShare(null); fetchData(true); }} />}
        {move && <MoveDialog me={me} nodes={move} onClose={(moved) => { setMove(null); if (moved) { setSelected(new Set()); fetchData(true); onChanged(); } }} />}
        {details && <Details me={me} node={details} onClose={() => setDetails(null)} onShare={() => setShare(details)} />}
      </Suspense>
      {rename && <RenameDialog node={rename} onClose={(changed) => { setRename(null); if (changed) { fetchData(true); onChanged(); } }} />}
      <ConfirmDialog open={!!forever} onClose={() => setForever(null)} onConfirm={async () => { if (forever) await deleteForever(forever); setForever(null); }} title="Delete for good?" confirmLabel="Delete for good" icon={<Trash2 aria-hidden />}>
        <p className="dz-muted">{forever?.length === 1 ? `“${forever[0].name}”` : `${forever?.length} items`} will be deleted for good, also for everyone it’s shared with. Deleting a folder deletes what you own in it, except inside folders that belong to other people. Other people’s files stay theirs. This can’t be undone.</p>
      </ConfirmDialog>
      <ConfirmDialog open={emptyTrash} onClose={() => setEmptyTrash(false)} onConfirm={async () => { await api('/api/trash/empty', { body: {} }).catch((e) => toast.show(e.message)); setEmptyTrash(false); fetchData(true); onChanged(); }} title="Empty the trash?" confirmLabel="Empty trash" icon={<Trash2 aria-hidden />}>
        <p className="dz-muted">Everything in the trash will be deleted for good. Deleting a folder deletes what you own in it, except inside folders that belong to other people. Other people’s files stay theirs. This can’t be undone.</p>
      </ConfirmDialog>
    </div>
  );
}

function CrumbLink({ c, onMoved }: { c: Crumb; onMoved(): void }) {
  const toast = useToast();
  const [over, setOver] = useState(false);
  return (
    <Link to={c.root ? '/' : `/folders/${c.id}`} className={`crumbs__link${over ? ' is-over' : ''}`}
      onDragOver={(e) => { if (e.dataTransfer.types.includes(DRAG_TYPE)) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={async (e) => { setOver(false); if (!e.dataTransfer.types.includes(DRAG_TYPE)) return; e.preventDefault(); if (await dropMove(e.dataTransfer, c.id, toast)) onMoved(); }}>
      {c.name}
    </Link>
  );
}
